import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useRole } from "@/data/RoleContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { CALL_OUTCOME_LABELS, QUICK_TEMPLATES, type Message, type CallOutcome, type MessageChannel } from "@/data/communicationTypes";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Send, Phone, PhoneCall, PhoneOff, MessageSquare, Clock, Copy,
  CheckCircle, XCircle, AlertCircle, ChevronDown, Zap, User,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { format } from "date-fns";
import { formatSafe } from "@/lib/dateUtils";
import { getCurrentRepName } from "@/lib/repIdentity";
import { ConversationService } from "@/services/conversation.service";

interface ConversationThreadProps {
  conversationId: string;
  contactName: string;
  contactPhone: string;
  compact?: boolean;
}

export function ConversationThread({ conversationId, contactName, contactPhone, compact }: ConversationThreadProps) {
  const { role } = useRole();
  const { profile } = useCurrentUser();
  const [threadMessages, setThreadMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [showCallLog, setShowCallLog] = useState(false);
  const [callOutcome, setCallOutcome] = useState<CallOutcome>("spoke_to_customer");
  const [callNotes, setCallNotes] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  // Load messages from Supabase and subscribe to realtime updates
  useEffect(() => {
    let cancelled = false;

    void ConversationService.fetchMessages(conversationId).then((msgs) => {
      if (!cancelled) setThreadMessages(msgs);
    });

    const unsub = ConversationService.subscribeToMessages((msgs) => {
      if (!cancelled) setThreadMessages(msgs);
    }, conversationId);

    return () => {
      cancelled = true;
      unsub();
    };
  }, [conversationId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [threadMessages.length]);

  const currentRep = profile?.name || (role === "sales" ? "Sales Rep" : "Admin");

  async function handleSendMessage() {
    if (!newMessage.trim()) return;
    const now = new Date().toISOString();
    const body = newMessage.trim();
    const optimisticId = `TEMP-${Date.now()}`;
    const optimistic: Message = {
      id: optimisticId,
      conversationId,
      direction: "outbound",
      channel: "sms",
      body,
      sender: currentRep,
      timestamp: now,
      deliveryStatus: "sent",
    };

    setThreadMessages((prev) => [...prev, optimistic]);
    setNewMessage("");

    const saved = await ConversationService.createMessage({
      conversationId,
      direction: "outbound",
      channel: "sms",
      body,
      sender: currentRep,
      timestamp: now,
      deliveryStatus: "sent",
    });

    if (saved) {
      setThreadMessages((prev) => prev.map((m) => m.id === optimisticId ? saved : m));
      void ConversationService.updateConversation(conversationId, { lastMessagePreview: body });
      toast.success("Message sent");
    } else {
      setThreadMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      toast.error("Failed to send message");
    }
  }

  async function handleLogCall() {
    const now = new Date().toISOString();
    const body = callNotes || `Call — ${CALL_OUTCOME_LABELS[callOutcome]}`;
    const optimisticId = `TEMP-${Date.now()}`;
    const optimistic: Message = {
      id: optimisticId,
      conversationId,
      direction: "outbound",
      channel: "call",
      body,
      sender: currentRep,
      timestamp: now,
      callOutcome,
      callDuration: callOutcome === "no_answer" || callOutcome === "failed" ? 0 : undefined,
    };

    setThreadMessages((prev) => [...prev, optimistic]);
    setShowCallLog(false);
    setCallNotes("");

    const saved = await ConversationService.createMessage({
      conversationId,
      direction: "outbound",
      channel: "call",
      body,
      sender: currentRep,
      timestamp: now,
      callOutcome,
      callDuration: callOutcome === "no_answer" || callOutcome === "failed" ? 0 : undefined,
    });

    if (saved) {
      setThreadMessages((prev) => prev.map((m) => m.id === optimisticId ? saved : m));
      void ConversationService.updateConversation(conversationId, { lastMessagePreview: `Call: ${CALL_OUTCOME_LABELS[callOutcome]}` });
      toast.success("Call logged");
    } else {
      setThreadMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      toast.error("Failed to log call");
    }
  }

  function handleClickToCall() {
    window.open(`tel:${contactPhone}`, "_self");
    setShowCallLog(true);
    toast.info(`Calling ${contactPhone}...`);
  }

  function handleUseTemplate(body: string) {
    const filled = body
      .replace("{name}", contactName.split(" ")[0])
      .replace("{rep}", currentRep);
    setNewMessage(filled);
  }

  function handleCopy(text: string) {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  }

  function getChannelIcon(channel: MessageChannel) {
    switch (channel) {
      case "sms": return MessageSquare;
      case "call": return PhoneCall;
      case "note": return User;
      case "system": return AlertCircle;
    }
  }

  function getDeliveryIcon(status?: string) {
    switch (status) {
      case "delivered": return <CheckCircle className="h-3 w-3 text-success" />;
      case "failed": return <XCircle className="h-3 w-3 text-destructive" />;
      case "sent": return <CheckCircle className="h-3 w-3 text-muted-foreground" />;
      default: return null;
    }
  }

  const maxHeight = compact ? "max-h-[300px]" : "max-h-[500px]";

  return (
    <div className="flex flex-col h-full">
      {/* Messages */}
      <div ref={scrollRef} className={cn("flex-1 overflow-y-auto p-3 space-y-3", maxHeight)}>
        {threadMessages.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p>No messages yet</p>
            <p className="text-xs mt-1">Send a message or log a call to start the conversation.</p>
          </div>
        ) : (
          threadMessages.map((msg) => {
            const isOutbound = msg.direction === "outbound";
            const isCall = msg.channel === "call";
            const ChannelIcon = getChannelIcon(msg.channel);
            const time = formatSafe(msg.timestamp, "MMM d, h:mm a");

            if (isCall) {
              return (
                <div key={msg.id} className="flex justify-center">
                  <div className="bg-muted rounded-lg px-4 py-2 max-w-[85%] text-center">
                    <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground mb-1">
                      <PhoneCall className="h-3 w-3" />
                      <span>{time}</span>
                      {msg.callOutcome && (
                        <Badge variant="outline" className="text-[9px] h-4">{CALL_OUTCOME_LABELS[msg.callOutcome]}</Badge>
                      )}
                    </div>
                    <p className="text-sm">{msg.body}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {msg.sender}
                      {msg.callDuration != null && msg.callDuration > 0 && ` · ${Math.floor(msg.callDuration / 60)}m ${msg.callDuration % 60}s`}
                    </p>
                  </div>
                </div>
              );
            }

            return (
              <div key={msg.id} className={cn("flex", isOutbound ? "justify-end" : "justify-start")}>
                <div
                  className={cn(
                    "rounded-2xl px-4 py-2.5 max-w-[80%] group relative",
                    isOutbound
                      ? "bg-primary text-primary-foreground rounded-br-md"
                      : "bg-muted text-foreground rounded-bl-md"
                  )}
                >
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.body}</p>
                  <div className={cn("flex items-center gap-1.5 mt-1", isOutbound ? "justify-end" : "justify-start")}>
                    <span className={cn("text-[10px]", isOutbound ? "text-primary-foreground/60" : "text-muted-foreground")}>
                      {time}
                    </span>
                    {isOutbound && getDeliveryIcon(msg.deliveryStatus)}
                  </div>
                  <button
                    onClick={() => handleCopy(msg.body)}
                    className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-background/20"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Input Area */}
      <div className="border-t p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" className="shrink-0 h-9 w-9" onClick={handleClickToCall} title="Call">
            <Phone className="h-4 w-4" />
          </Button>

          <Popover>
            <PopoverTrigger asChild>
              <Button size="icon" variant="outline" className="shrink-0 h-9 w-9" title="Templates">
                <Zap className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2" align="start">
              <p className="text-xs font-medium text-muted-foreground px-2 mb-1">Quick Templates</p>
              {QUICK_TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => handleUseTemplate(t.body)}
                  className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-muted transition-colors"
                >
                  {t.label}
                </button>
              ))}
            </PopoverContent>
          </Popover>

          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="h-9 flex-1"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void handleSendMessage(); } }}
          />
          <Button size="icon" className="shrink-0 h-9 w-9" onClick={() => void handleSendMessage()} disabled={!newMessage.trim()}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Call Log Dialog */}
      <Dialog open={showCallLog} onOpenChange={setShowCallLog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Log Call Result</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Outcome</p>
              <Select value={callOutcome} onValueChange={(v) => setCallOutcome(v as CallOutcome)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.entries(CALL_OUTCOME_LABELS) as [CallOutcome, string][]).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Notes</p>
              <Textarea value={callNotes} onChange={(e) => setCallNotes(e.target.value)} rows={2} placeholder="What was discussed..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCallLog(false)}>Skip</Button>
            <Button onClick={() => void handleLogCall()}>Save Call</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
